import './CircularProgress.scss';
import { useCircularProgressAnimation } from '../../../hooks/useCircularProgressAnimation';

/**
 * Props for the CircularProgress component.
 * @property size - The diameter of the circular progress spinner in pixels.
 * @property variant - Visual color style for the spinner. Can be 'yellow', 'black', or 'white'.
 */
export interface CircularProgressProps {
  /**
   * The diameter of the circular progress spinner in pixels.
   */
  size: number;
  /**
   * Visual color style for the spinner.
   * Can be 'yellow', 'black', or 'white'.
   */
  variant: 'yellow' | 'black' | 'white';
}

/**
 * CircularProgress
 *
 * Renders a visually simple, animated circular progress spinner using SVG.
 * All geometry calculations and animation logic are encapsulated
 * within the `useCircularProgressAnimation` custom hook.
 *
 * Accessibility:
 * - Uses `role="progressbar"` and `aria-label="loading"` for screen reader support.
 *
 * @param props CircularProgressProps
 * @returns JSX.Element
 *
 * @example
 * <CircularProgress size={40} variant="yellow" />
 */
export const CircularProgress = ({
  size,
  variant,
}: CircularProgressProps): JSX.Element => {
  const { svgProps, circleProps } = useCircularProgressAnimation(size);

  return (
    <span
      className={`circular-progress circular-progress--${variant}`}
      style={{ width: size, height: size }}
      role='progressbar'
      aria-label='loading'
      data-testid='circular-progress'
    >
      <svg className='circular-progress__svg' {...svgProps}>
        <circle className='circular-progress__circle' {...circleProps} />
      </svg>
    </span>
  );
};


export default CircularProgress